import { useId, useRef, useState } from "react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { AlertTriangle, Check, FileUp, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

import {
  loadMyCard,
  removeMyDoc,
  saveMyRequisites,
  submitMyRequisites,
  uploadMyDoc,
} from "@/braining/partners.functions";
import { crossChecks, mergeFound, MAX_FILE_BYTES } from "@/braining/partner-docs";
import { registryChecks } from "@/braining/registry";
import { fieldsOf, shown } from "@/braining/partner-form";
import { prepareUpload } from "@/braining/shrink-image";
import { KIND_LABEL } from "@/braining/partners";
import type { PartnerCard } from "@/braining/partners-store";
import { DOC_FLOWS, DOCS, FORM, type Problems, type Values } from "@/braining/partner-form";
import { CONSENT_TEXT, REQ_STATE_LABEL } from "@/braining/partner-requisites";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { RequisitesField } from "@/braining/components/requisites-field";

/**
 * Анкета подрядчика: реквизиты один раз — дальше они подставляются в
 * договоры сами.
 *
 * Черновик сохраняется с пустыми полями, но не с опечатками: неверный ИНН
 * или счёт, не сходящийся с БИК, подсвечиваются сразу, пока человек смотрит
 * на поле. Отправка на проверку — с согласием на обработку данных.
 *
 * Документы (этап 2б): приложил — модель прочитала — пустые поля анкеты
 * заполнились и сохранились сами. Заполненное по-другому не перетираем, а
 * показываем рядом с кнопкой «Взять из документа»: решает человек.
 */
export const Route = createFileRoute("/cabinet_/requisites")({
  head: () => ({ meta: [{ title: "Реквизиты — кабинет партнёра Braining" }] }),
  loader: () => loadMyCard(),
  component: RequisitesPage,
});

function RequisitesPage() {
  const initial = Route.useLoaderData();
  if (!initial) {
    return (
      <p className="text-sm text-muted-foreground">
        Карточка не найдена. Напишите тому, кто вас приглашал.
      </p>
    );
  }
  return <RequisitesForm initial={initial} />;
}

function RequisitesForm({ initial }: { initial: PartnerCard }) {
  const uid = useId();
  const router = useRouter();
  const [card, setCard] = useState(initial);
  // Почта для входа — подставляем в «Связь», если там пусто: вписывать её
  // второй раз незачем. Сохранится вместе с формой.
  const [values, setValues] = useState<Values>(() => ({
    ...initial.requisites.values,
    email: initial.requisites.values.email || initial.email || "",
    // Самозанятые почти никогда не формируют УПД в XML (Даша, 27.10) —
    // по умолчанию бумага; поменять можно здесь же.
    docFlow:
      initial.requisites.values.docFlow || (initial.kind === "self_employed" ? DOC_FLOWS[1] : ""),
  }));
  const [problems, setProblems] = useState<Problems>({});
  const [error, setError] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState<"save" | "submit" | null>(null);
  const [conflicts, setConflicts] = useState<{ key: string; mine: string; doc: string }[]>([]);

  const req = card.requisites;
  const dirty = JSON.stringify(values) !== JSON.stringify(req.values);
  const sent = req.state === "submitted" || req.state === "approved";

  async function save(next: Values = values): Promise<PartnerCard | null> {
    const res = await saveMyRequisites({ data: { values: next } });
    if (!res.ok) {
      setError(res.error);
      setProblems(res.problems ?? {});
      return null;
    }
    setError(null);
    setProblems({});
    setCard(res.next);
    setValues(res.next.requisites.values);
    return res.next;
  }

  async function onSave() {
    setBusy("save");
    try {
      if (await save()) toast.success("Сохранено");
    } catch (e) {
      console.error("[requisites] не удалось сохранить", e);
      setError("Не удалось сохранить. Попробуйте ещё раз.");
    } finally {
      setBusy(null);
    }
  }

  async function onSubmit() {
    setBusy("submit");
    try {
      if (dirty && !(await save())) return;
      const res = await submitMyRequisites({ data: { agreed } });
      if (!res.ok) {
        setError(res.error);
        setProblems(res.problems ?? {});
        return;
      }
      setCard(res.next);
      setError(null);
      setProblems({});
      toast.success("Отправлено на проверку");
      await router.invalidate();
    } catch (e) {
      console.error("[requisites] не удалось отправить", e);
      setError("Не удалось отправить. Попробуйте ещё раз.");
    } finally {
      setBusy(null);
    }
  }

  const set = (key: string, v: string) => setValues((prev) => ({ ...prev, [key]: v }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Реквизиты</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {KIND_LABEL[card.kind]} · {REQ_STATE_LABEL[req.state].toLowerCase()}
          {sent && " · если поменяете поле, реквизиты уйдут на повторную проверку"}
        </p>
      </div>

      {req.state === "returned" && req.review?.note && (
        <p className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning-ink">
          Что поправить: {req.review.note}
        </p>
      )}

      <DocsPanel
        card={card}
        values={values}
        onCard={setCard}
        onFound={async (found) => {
          const merged = mergeFound(values, found);
          setValues(merged.values);
          setConflicts(merged.conflicts);
          if (merged.filled.length) {
            const saved = await save(merged.values);
            if (saved) toast.success(`Из документа заполнено полей: ${merged.filled.length}`);
          } else if (!merged.conflicts.length) {
            toast.success("Документ прочитан — всё совпало с анкетой");
          }
        }}
      />

      {conflicts.length > 0 && (
        <Card className="border-warning/50">
          <CardContent className="space-y-2 p-5">
            <div className="font-medium">В документе не так, как в анкете</div>
            {conflicts.map((c) => (
              <div key={c.key} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-muted-foreground">{labelOf(card, c.key)}:</span>
                <span>в анкете {c.mine},</span>
                <span>в документе {c.doc}</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    set(c.key, c.doc);
                    setConflicts((list) => list.filter((x) => x.key !== c.key));
                  }}
                >
                  Взять из документа
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {FORM[card.kind].map((section) => (
        <Card key={section.title}>
          <CardContent className="space-y-4 p-5">
            <h2 className="font-medium">{section.title}</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {section.fields
                .filter((f) => shown(f, values))
                .map((f) => (
                  <RequisitesField
                    key={f.key}
                    id={`${uid}-${f.key}`}
                    spec={f}
                    value={values[f.key] ?? ""}
                    problem={problems[f.key]}
                    onChange={(v) => set(f.key, v)}
                  />
                ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {error && (
        <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive-ink">
          {error}
        </p>
      )}

      <Card>
        <CardContent className="space-y-4 p-5">
          {!sent && (
            <label htmlFor={`${uid}-consent`} className="flex items-start gap-3 text-sm">
              <Checkbox
                id={`${uid}-consent`}
                checked={agreed}
                onCheckedChange={(v) => setAgreed(v === true)}
                className="mt-0.5"
              />
              <span className="text-muted-foreground">{CONSENT_TEXT}</span>
            </label>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => void onSave()}
              disabled={busy !== null || !dirty}
            >
              {busy === "save" && <Loader2 className="size-4 animate-spin" />}
              Сохранить
            </Button>
            {sent ? (
              dirty && (
                <span className="self-center text-xs text-muted-foreground">
                  После сохранения реквизиты нужно будет отправить заново.
                </span>
              )
            ) : (
              <Button onClick={() => void onSubmit()} disabled={busy !== null || !agreed}>
                {busy === "submit" && <Loader2 className="size-4 animate-spin" />}
                Отправить на проверку
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function labelOf(card: PartnerCard, key: string): string {
  return fieldsOf(card.kind).find((f) => f.key === key)?.label ?? key;
}

/**
 * Документы: приложить, увидеть, что прочитано, и что не сходится.
 * Подрядчику показываем только то, что надо исправить, — «всё сошлось»
 * нужно Даше, а не ему.
 */
function DocsPanel({
  card,
  values,
  onCard,
  onFound,
}: {
  card: PartnerCard;
  values: Values;
  onCard: (c: PartnerCard) => void;
  onFound: (found: Values) => Promise<void>;
}) {
  const recognized = card.requisites.recognized ?? {};
  const issues = [
    ...crossChecks(card.kind, values, recognized),
    ...registryChecks(card.kind, values, card.requisites.registry),
  ].filter((c) => c.level === "bad");

  return (
    <Card>
      <CardContent className="space-y-4 p-5">
        <div>
          <div className="flex items-center gap-2 font-medium">
            <FileUp className="size-4 text-muted-foreground" />
            Документы
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Приложите — и анкета заполнится сама. Фото с телефона подойдёт, PDF — тоже.
          </p>
        </div>
        <div className="divide-y rounded-md border">
          {DOCS[card.kind].map((spec) => (
            <DocRow
              key={spec.key}
              docKind={spec.key}
              label={spec.label}
              hint={spec.hint}
              required={spec.required}
              done={recognized[spec.key]}
              onCard={onCard}
              onFound={onFound}
            />
          ))}
        </div>
        {issues.length > 0 && (
          <ul className="space-y-1 text-sm text-destructive-ink">
            {issues.map((c) => (
              <li key={c.text} className="flex gap-2">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                {c.text}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function DocRow({
  docKind,
  label,
  hint,
  required,
  done,
  onCard,
  onFound,
}: {
  docKind: string;
  label: string;
  hint: string;
  required: boolean;
  done?: { fileName: string; at: string; stored: boolean };
  onCard: (c: PartnerCard) => void;
  onFound: (found: Values) => Promise<void>;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function upload(file: File) {
    setBusy(true);
    setError(null);
    try {
      const prepared = await prepareUpload(file);
      if (prepared.bytes > MAX_FILE_BYTES) {
        setError("Файл больше 10 МБ — пришлите фото или PDF поменьше");
        return;
      }
      const res = await uploadMyDoc({
        data: { docKind, name: prepared.name, base64: prepared.base64 },
      });
      if (!res.ok) {
        setError(res.error);
        return;
      }
      onCard(res.card);
      await onFound(res.found);
    } catch (e) {
      console.error("[requisites] не удалось загрузить документ", e);
      setError("Не удалось загрузить. Попробуйте ещё раз.");
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      const res = await removeMyDoc({ data: { docKind } });
      if (res.ok) onCard(res.next);
      else setError(res.error);
    } catch (e) {
      console.error("[requisites] не удалось убрать документ", e);
      setError("Не удалось убрать — обновите страницу и попробуйте ещё раз");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-1.5 px-3 py-3">
      <div className="flex flex-wrap items-center gap-2">
        {done ? (
          <Check className="size-4 text-primary" />
        ) : (
          <FileUp className="size-4 text-muted-foreground" />
        )}
        <span className="text-sm font-medium">{label}</span>
        {!required && <span className="text-xs text-muted-foreground">если есть</span>}
        <div className="ml-auto flex gap-1">
          <input
            ref={input}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void upload(f);
            }}
          />
          <Button
            size="sm"
            variant={done ? "ghost" : "outline"}
            disabled={busy}
            onClick={() => input.current?.click()}
          >
            {busy && <Loader2 className="size-4 animate-spin" />}
            {busy ? "Читаем…" : done ? "Заменить" : "Приложить"}
          </Button>
          {done && (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => void remove()}
              aria-label={`Убрать: ${label}`}
            >
              <Trash2 className="size-4" />
            </Button>
          )}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        {done
          ? `${done.fileName} · ${new Date(done.at).toLocaleDateString("ru-RU")}${
              done.stored ? "" : " · прочитали, сам скан не храним"
            }`
          : hint}
      </p>
      {error && <p className="text-xs text-destructive-ink">{error}</p>}
    </div>
  );
}
